import React from "react";
import SiteHeader from "../components/SiteHeader";
import Reveal from "../components/Reveal";
import {
  IconArrowRight,
  IconBolt,
  IconErrand,
  IconFood,
  IconGrocery,
  IconPin,
  IconReceipt,
  IconRide,
  IconRoute,
  IconWallet,
} from "../components/Icons";
import { SIGNUP_URL } from "../siteConfig";
import { useDocumentMeta } from "../useDocumentMeta";
import "../styles/about.css";

const PRINCIPLES = [
  {
    icon: <IconReceipt size={21} />,
    title: "Clear details",
    body: "Every booking shows the details and the fare before you book.",
  },
  {
    icon: <IconRoute size={21} />,
    title: "Fair fares",
    body: "Distance-based fares, the same for everyone, with the estimate shown before you confirm.",
  },
  {
    icon: <IconBolt size={21} />,
    title: "Fast updates",
    body: "Live status tracking so you know where your order or errand is at any time.",
  },
];

const GLANCE = [
  { icon: <IconFood size={18} />, label: "Four services — Foods, Pabili, Pasugo, Angkas" },
  { icon: <IconRide size={18} />, label: "Angkas rides, with a free helmet on every trip" },
  { icon: <IconReceipt size={18} />, label: "Transparent, distance-based fares" },
  { icon: <IconWallet size={18} />, label: "Cash on delivery or GCash — no cards" },
  { icon: <IconPin size={18} />, label: "Built for the Philippines" },
];

const About: React.FC = () => {
  useDocumentMeta({
    title: "About PABS — Pickup And Bring Stuff",
    description:
      "PABS (Pickup And Bring Stuff) is an all-in-one platform for food and drinks, Pabili shopping, Pasugo errands, and Angkas rides in the Philippines.",
    canonicalPath: "/about",
    ogImage: "https://pabs.online/logo512.png",
    jsonLdId: "pabs-about-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About PABS",
      url: "https://pabs.online/about",
      mainEntity: {
        "@type": "Organization",
        name: "PABS",
        alternateName: "Pickup And Bring Stuff",
        url: "https://pabs.online/",
        logo: "https://pabs.online/logo512.png",
      },
    },
  });

  return (
    <>
      <SiteHeader current="/about" />

      <main id="main">
        {/* ================= Hero ================= */}
        <section className="ab-hero">
          <div className="ab-hero__bg" aria-hidden="true" />
          <div className="u-container ab-hero__inner">
            <Reveal className="ab-hero__copy">
              <span className="t-eyebrow">About us</span>
              <h1 className="t-h1 ab-hero__title">
                Deliveries and errands —<br />
                <span className="t-grad">made simple.</span>
              </h1>
              <p className="t-lead ab-hero__lead">
                PABS is an all-in-one app for Foods, Pabili shopping, Pasugo
                errands, and Angkas rides. We focus on the
                essentials: clear pickup and drop-off details, transparent fare
                estimates, and fast status updates without clutter.
              </p>
              <div className="btn-row">
                <a className="btn btn--primary btn--lg" href={SIGNUP_URL}>
                  Create account
                  <IconArrowRight size={18} className="btn__icon btn__icon--arrow" />
                </a>
                <a className="btn btn--secondary btn--lg" href="/#services">
                  See services
                </a>
              </div>
            </Reveal>

            <Reveal delay={120} className="ab-hero__aside">
              <div className="glance">
                <h2 className="glance__title">At a glance</h2>
                <ul className="glance__list">
                  {GLANCE.map((g) => (
                    <li key={g.label}>
                      <span className="glance__icon">{g.icon}</span>
                      {g.label}
                    </li>
                  ))}
                </ul>
                <div className="glance__mark">
                  <img src="/pabs-mark.png" alt="" width={56} height={56} loading="lazy" />
                  <p>
                    <strong>PABS</strong> — <em>Pickup And Bring Stuff</em>.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ================= Story ================= */}
        <section className="u-section" id="how-we-started">
          <div className="u-container">
            <div className="story">
              <Reveal className="story__lede">
                <span className="t-eyebrow">Our story</span>
                <h2 className="t-h2" style={{ marginTop: 14 }}>
                  How we started
                </h2>
              </Reveal>

              <Reveal delay={80} className="story__body">
                <p className="t-lead">
                  PABS started as a way to make everyday delivery more dependable
                  — one place to order food, shop groceries and pharmacy items,
                  or send a rider on an errand without jumping between apps.
                </p>
              </Reveal>
            </div>

            <div className="grid grid--3 ab-principles">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.title} delay={i * 90}>
                  <article className="card card--hover ab-principle">
                    <span className="icon-tile icon-tile--violet">{p.icon}</span>
                    <h3 className="card__title">{p.title}</h3>
                    <p className="card__body">{p.body}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ================= Why PABS (ink) ================= */}
        <section className="u-section u-section--ink" id="why-pabs">
          <div className="u-container">
            <div className="why">
              <Reveal className="why__copy">
                <span className="t-eyebrow">Why PABS</span>
                <h2 className="t-h2" style={{ marginTop: 14 }}>
                  A simple idea behind
                  <br />a growing platform.
                </h2>
                <p className="t-lead why__lead">
                  <strong>PABS: Pickup And Bring Stuff.</strong> It reflects
                  what the platform is designed to do — keep customers, riders,
                  and stores aligned on the same request details,
                  whether you're moving meals, groceries, pharmacy items, or items
                  across town.
                </p>
              </Reveal>

              <Reveal delay={120} className="why__cards">
                <div className="why-card">
                  <span className="icon-tile icon-tile--on-dark">
                    <IconFood size={20} />
                  </span>
                  <div>
                    <h3 className="t-h4">Foods</h3>
                    <p>Restaurants and cafés, tracked end to end.</p>
                  </div>
                </div>
                <div className="why-card">
                  <span className="icon-tile icon-tile--on-dark">
                    <IconGrocery size={20} />
                  </span>
                  <div>
                    <h3 className="t-h4">Pabili</h3>
                    <p>Groceries, pharmacy, and everyday essentials.</p>
                  </div>
                </div>
                <div className="why-card">
                  <span className="icon-tile icon-tile--on-dark">
                    <IconErrand size={20} />
                  </span>
                  <div>
                    <h3 className="t-h4">Pasugo</h3>
                    <p>Buy-and-deliver, or send an item across town.</p>
                  </div>
                </div>
                <div className="why-card">
                  <span className="icon-tile icon-tile--on-dark">
                    <IconRide size={20} />
                  </span>
                  <div>
                    <h3 className="t-h4">Angkas</h3>
                    <p>A rider takes you across town, helmet included.</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About;
